const normalizeSearchValue = (value = "") =>
  String(value).toLowerCase().trim();

const matchesSearchValue = (searchValue, ...values) => {
  const normalizedSearch = normalizeSearchValue(searchValue);

  if (!normalizedSearch) {
    return true;
  }

  return values.some((value) =>
    normalizeSearchValue(value).includes(normalizedSearch)
  );
};

const collectMarketSearchValues = (value) => {
  if (value == null) {
    return [];
  }

  if (Array.isArray(value)) {
    return value.flatMap(collectMarketSearchValues);
  }

  if (typeof value === "object") {
    return Object.values(value).flatMap(collectMarketSearchValues);
  }

  return [String(value)];
};

const getMarketChildSearchValues = (market = {}) => [
  market.name,
  market.shortName,
  market.displayName,
  market.code,
  market.symbol,
  market.marketId,
  ...collectMarketSearchValues(market.marketAttributes),
].filter(Boolean);

const getMarketParentSearchValues = (market = {}) =>
  [market.productName, market.productShortName].filter(Boolean);

const getMarketSearchState = (markets = [], searchValue = "") => {
  const normalizedSearch = normalizeSearchValue(searchValue);

  if (!normalizedSearch) {
    return {
      markets,
      autoExpandedProductIds: [],
      hasMatches: markets.length > 0,
    };
  }

  const matchingProductIds = new Set(
    markets
      .filter((market) =>
        matchesSearchValue(
          normalizedSearch,
          ...getMarketParentSearchValues(market)
        )
      )
      .map((market) => market.productId)
  );

  const filteredMarkets = markets.filter(
    (market) =>
      matchingProductIds.has(market.productId) ||
      matchesSearchValue(
        normalizedSearch,
        ...getMarketChildSearchValues(market),
        ...getMarketParentSearchValues(market)
      )
  );

  const autoExpandedProductIds = Array.from(
    new Set(
      filteredMarkets
        .filter((market) =>
          matchesSearchValue(
            normalizedSearch,
            ...getMarketChildSearchValues(market)
          )
        )
        .map((market) => market.productId)
    )
  );

  return {
    markets: filteredMarkets,
    autoExpandedProductIds,
    hasMatches: filteredMarkets.length > 0,
  };
};

const filterMarketsBySearch = (markets = [], searchValue = "") => {
  return getMarketSearchState(markets, searchValue).markets;
};

const getDescriptorSearchText = (item = {}) =>
  item.searchText || item.title || item.text || item.label || item.name || "";

const filterNavigationItem = ({
  item,
  searchValue,
  includeEntireBranch = false,
}) => {
  if (!item) {
    return {
      item: null,
      autoExpandedMenuIds: [],
      subtreeHasMatch: false,
    };
  }

  if (item.kind === "marketSelector") {
    const { markets: filteredMarkets, autoExpandedProductIds, hasMatches } =
      getMarketSearchState(item.markets, searchValue);

    if (!includeEntireBranch && !hasMatches) {
      return {
        item: null,
        autoExpandedMenuIds: [],
        subtreeHasMatch: false,
      };
    }

    return {
      item: {
        ...item,
        markets: includeEntireBranch ? item.markets : filteredMarkets,
        enableSearch: false,
        autoExpandedProductIds,
        navigationSearchValue: searchValue,
      },
      autoExpandedMenuIds: [],
      subtreeHasMatch: hasMatches,
    };
  }

  const selfMatches = matchesSearchValue(
    searchValue,
    getDescriptorSearchText(item)
  );
  const keepEntireBranch = includeEntireBranch || selfMatches;

  if (item.kind === "submenu") {
    const nextGroups = [];
    const autoExpandedMenuIds = [];
    let descendantHasMatch = false;

    (item.groups || []).forEach((group) => {
      const nextItems = [];

      (group.items || []).forEach((childItem) => {
        const filteredChild = filterNavigationItem({
          item: childItem,
          searchValue,
          includeEntireBranch: keepEntireBranch,
        });

        if (filteredChild.item) {
          nextItems.push(filteredChild.item);
        }

        if (filteredChild.subtreeHasMatch) {
          descendantHasMatch = true;
        }

        autoExpandedMenuIds.push(...filteredChild.autoExpandedMenuIds);
      });

      if (nextItems.length > 0) {
        nextGroups.push({
          ...group,
          items: nextItems,
        });
      }
    });

    if (!keepEntireBranch && nextGroups.length === 0) {
      return {
        item: null,
        autoExpandedMenuIds: [],
        subtreeHasMatch: false,
      };
    }

    return {
      item: {
        ...item,
        groups: nextGroups,
      },
      autoExpandedMenuIds:
        descendantHasMatch && item.id
          ? [item.id, ...autoExpandedMenuIds]
          : autoExpandedMenuIds,
      subtreeHasMatch: selfMatches || descendantHasMatch,
    };
  }

  if (!keepEntireBranch) {
    return {
      item: null,
      autoExpandedMenuIds: [],
      subtreeHasMatch: false,
    };
  }

  return {
    item,
    autoExpandedMenuIds: [],
    subtreeHasMatch: selfMatches,
  };
};

const filterNavigationItems = ({
  items = [],
  isSearching,
  searchValue,
}) => {
  if (!isSearching) {
    return {
      items: (items || []).filter(Boolean),
      autoExpandedMenuIds: [],
    };
  }

  const filteredItems = [];
  const autoExpandedMenuIds = [];

  (items || []).forEach((item) => {
    const filteredItem = filterNavigationItem({
      item,
      searchValue,
      includeEntireBranch: false,
    });

    if (filteredItem.item) {
      filteredItems.push(filteredItem.item);
    }

    autoExpandedMenuIds.push(...filteredItem.autoExpandedMenuIds);
  });

  return {
    items: filteredItems,
    autoExpandedMenuIds: Array.from(new Set(autoExpandedMenuIds)),
  };
};

export {
  filterNavigationItems,
  filterMarketsBySearch,
  getMarketSearchState,
  matchesSearchValue,
};
